import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
  assessmentsApi,
  complianceApi,
  type Assessment,
  type CompliancePreviewResponse,
  type FilingRequirement,
} from '../lib/api';
import { useUserPermissions } from '../hooks/useUserPermissions';

const PRIORITY_COLOR: Record<FilingRequirement['priority'], 'error' | 'warning' | 'info' | 'success'> = {
  required: 'error',
  likely: 'warning',
  conditional: 'info',
  recommended: 'success',
};

export default function AssessmentDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWriteAssessments } = useUserPermissions();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [report, setReport] = useState<CompliancePreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [explaining, setExplaining] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    assessmentsApi
      .get(id)
      .then(setAssessment)
      .catch(() => setError('Failed to load assessment'))
      .finally(() => setLoading(false));
  }, [id]);

  const replay = async (explain = false) => {
    if (!id) return;
    setExplaining(true);
    setError('');
    try {
      if (explain && assessment) {
        const explained = await complianceApi.preview(
          {
            entity: assessment.params.entity,
            states: assessment.params.states ?? [],
            fundraisingStates: assessment.params.fundraisingStates ?? [],
            revenue: assessment.params.revenue,
            assets: assessment.params.assets,
            fundraising: assessment.params.fundraising ?? [],
            hasEmployees: assessment.params.hasEmployees ?? [],
            isChurch: assessment.params.isChurch ?? null,
            usesProfessionalFundraiser: assessment.params.usesProfessionalFundraiser ?? 'no',
            conductRaffles: assessment.params.conductRaffles ?? 'no',
          },
          { explain: true }
        );
        setReport(explained);
      } else {
        const replayed = await assessmentsApi.replay(id);
        setReport(replayed);
      }
    } catch {
      setError('Failed to replay');
    } finally {
      setExplaining(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (!assessment) return <Alert severity="error">Not found</Alert>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/assessments')} size="small" sx={{ ml: -1, mb: 0.5 }}>
            Back
          </Button>
          <Typography variant="h5" fontWeight={700}>
            Assessment · {new Date(assessment.requestedAt).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {assessment.metadata.apiKeyLabel} · {assessment.responseTimeMs ?? '—'} ms ·{' '}
            {assessment.resultSummary?.total ?? '—'} filings
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {canWriteAssessments && (
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={() => replay(false)}
              disabled={explaining}
            >
              Replay
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PsychologyIcon />}
            onClick={() => replay(true)}
            disabled={explaining}
          >
            {explaining ? 'Working…' : 'Explain'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Logged inputs
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`entity: ${assessment.params.entity}`} />
          <Chip size="small" label={`revenue: ${assessment.params.revenue}`} />
          <Chip size="small" label={`assets: ${assessment.params.assets}`} />
          {assessment.params.isChurch && <Chip size="small" label={`isChurch: ${assessment.params.isChurch}`} />}
          {assessment.params.usesProfessionalFundraiser && (
            <Chip size="small" label={`usesProfessionalFundraiser: ${assessment.params.usesProfessionalFundraiser}`} />
          )}
          {assessment.params.conductRaffles && (
            <Chip size="small" label={`conductRaffles: ${assessment.params.conductRaffles}`} />
          )}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">States</Typography>
            <Typography variant="body2">{assessment.params.states?.join(', ') || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Fundraising states</Typography>
            <Typography variant="body2">{assessment.params.fundraisingStates?.join(', ') || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Fundraising methods</Typography>
            <Typography variant="body2">{assessment.params.fundraising?.join(', ') || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Workers</Typography>
            <Typography variant="body2">{assessment.params.hasEmployees?.join(', ') || '—'}</Typography>
          </Box>
        </Stack>
      </Paper>

      {report && (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={`Filings (${report.filings.length})`} />
            <Tab label="Counts" />
            <Tab label="Raw JSON" />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={1.5}>
              {report.filings.map((f, i) => (
                <Paper key={`${f.title}-${i}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {f.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {f.description}
                      </Typography>
                      {f.reasons && f.reasons.length > 0 && (
                        <Box mt={1.5}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Triggered by:
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.5} useFlexGap>
                            {f.reasons.map((r, ri) => (
                              <Chip key={ri} size="small" variant="outlined" label={r} />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Chip size="small" label={f.priority} color={PRIORITY_COLOR[f.priority]} />
                      {f.state && <Typography variant="caption">{f.state}</Typography>}
                      {f.deadline && <Typography variant="caption" color="text.secondary">{f.deadline}</Typography>}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          {tab === 1 && (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(report.counts).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell>{k}</TableCell>
                      <TableCell align="right"><strong>{v}</strong></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {tab === 2 && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: '#0f172a',
                color: '#e2e8f0',
                borderRadius: 1,
                fontSize: 12.5,
                fontFamily: 'monospace',
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(report, null, 2)}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
